/* eslint-disable no-plusplus */
import * as THREE from 'three';
// import { BloomEffect, EffectComposer, EffectPass, RenderPass, ShaderPass, PixelationEffect, DotScreenEffect, SavePass, SMAAEffect, SSAOEffect, NormalPass } from "postprocessing";
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { downloadCanvas, downloadDataTextureAsPNG, gray2gradient } from './geometry.js';

import { tic, toc, accumulateRenderTargets } from './utils.js';

export default class Shader {
  resolution = 1.0;
  scale = 5.0;
  minDSM = 0.0;
  maxDSM = 0.0;
  displacementBias = 0.0;
  dsmTexture = null;
  ranges = [];
  weather = null;
  center = null;
  sun = null;
  ambient = null;
  ios = false;
  renderer = null;
  canvasPool = null;
  byServerSideData = true;
  debugMode = false;
  channels = 1;

  constructor(resolution, scale, minDSM, maxDSM, displacementBias, dsmTexture, ranges, weather, center) {
    this.resolution = resolution;
    this.scale = scale;
    this.minDSM = minDSM;
    this.maxDSM = maxDSM;
    this.displacementBias = displacementBias;
    this.dsmTexture = dsmTexture;
    this.ranges = ranges;
    this.weather = weather;
    this.center = center;
  }

  placeImage = (sourceArray, targetArray, sourceWidth, sourceHeight, targetWidth, targetHeight, startX, startY) => {
    // Ensure that the source image fits within the target image starting from (startX, startY)
    if (startX + sourceWidth > targetWidth || startY + sourceHeight > targetHeight) {
      throw new Error('The source image will not fit at the specified start coordinates.');
    }

    // Loop through every pixel in the source image
    for (let y = 0; y < sourceHeight; y++) {
      for (let x = 0; x < sourceWidth; x++) {
        // Calculate the index for the source and target arrays
        const sourceIndex = ((sourceHeight - y - 1) * sourceWidth + x) * this.channels;
        const targetIndex = ((startY + y) * targetWidth + (startX + x)) * this.channels;
        targetArray[targetIndex] = sourceArray[sourceIndex];
      }
    }
  };

  setRenderer(renderer) {
    this.renderer = renderer;
  }

  setCanvasPool(canvasPool) {
    this.canvasPool = canvasPool;
  }

  canBlendFloatRenderTarget = () => {
    const gl = this.renderer.getContext();
    return (
      (this.renderer.capabilities.isWebGL2 || gl.getExtension('EXT_color_buffer_float')) &&
      !!gl.getExtension('EXT_float_blend')
    );
  };

  newRenderer = (width, height) => {
    if (!this.canBlendFloatRenderTarget()) {
      return new THREE.WebGLRenderTarget(width, height, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        encoding: THREE.LinearEncoding,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        depthBuffer: true,
        stencilBuffer: false
      });
    }

    // const renderTarget = new THREE.WebGLRenderTarget(width, height, { type: THREE.UnsignedByteType, encoding: THREE.LinearEncoding, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, format: THREE.RGBAFormat });
    const renderTarget = new THREE.WebGLRenderTarget(width, height, {
      format: THREE.RedFormat, // *RefFormat
      type: THREE.FloatType,
      encoding: THREE.LinearEncoding,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: true,
      stencilBuffer: false
    });
    return renderTarget;
  };

  changeMaterial = (parent, newParent, iterCount, changeScene) => {
    let isroof = false;
    if (!['treeTrunk', 'treeBranch'].includes(newParent.name)) {
      const obstacle = newParent.name === 'roofPlane' && parent.parent.name === '';
      const ground = newParent.name === 'groundPlane';

      if (obstacle) {
        newParent.material = new THREE.MeshBasicMaterial({
          color: 0x000000,
          side: THREE.DoubleSide
        });
      } else if (ground) {
        const dispScale = (100 / (this.resolution / this.scale)) * (this.maxDSM - this.minDSM);
        const dispBias = -(this.displacementBias / 255) * dispScale;

        newParent.material = new THREE.MeshStandardMaterial({
          color: 0x000000,
          // transparent: true,
          // opacity: 1 / iterCount,
          side: THREE.DoubleSide,
          displacementMap: this.dsmTexture,
          displacementScale: dispScale,
          displacementBias: dispBias
        });
        newParent.customDepthMaterial = new THREE.MeshDepthMaterial({
          depthPacking: THREE.RGBADepthPacking,
          displacementMap: this.dsmTexture,
          displacementScale: dispScale,
          displacementBias: dispBias
        });
      } else {
        isroof = true;
        newParent.material = new THREE.MeshLambertMaterial({
          color: newParent.name === 'roofPlane' ? 0xffffff : 0x000000,
          // transparent: true,
          // opacity: 1 / iterCount,
          side: THREE.DoubleSide
        });
      }

      if (changeScene) {
        parent.material = newParent.material.clone();
      }

      newParent.material.blending = THREE.AdditiveBlending;
    } else {
      newParent.material = new THREE.MeshBasicMaterial({ color: 0x000000 });
      newParent.material.colorWrite = false;
      newParent.material.depthWrite = false;
      newParent.material.side = THREE.FrontSide;
    }

    return isroof;
  };

  cloneScene = (shadingType, iterCount, parent, changeScene = false, isRoot = false) => {
    if (!parent) return null;

    const newParent = parent.clone();
    const isTSRF = shadingType === 'TSRF';
    const isTOF = shadingType === 'TOF';
    const isSolarAccess = shadingType === 'SolarAccess';

    if (isRoot) newParent.background = null;

    let isRoof = false;
    if (newParent.material) {
      isRoof = this.changeMaterial(parent, newParent, iterCount, changeScene);
    }

    if (!newParent.name.includes('Light')) {
      newParent.castShadow = !isTOF;
      newParent.receiveShadow = !isTOF && isRoof;
      if (changeScene) {
        parent.castShadow = !isTOF;
        parent.receiveShadow = !isTOF && isRoof;
      }
    }

    const [minx, miny, maxx, maxy, maxz] = this.ranges;
    const [rwidth, rheight] = [maxx - minx, maxy - miny];

    for (let i = newParent.children.length - 1; i >= 0; i -= 1) {
      const obj = newParent.children[i];
      const obstacle = obj.name === 'roofPlane' && obj.parent.name === '';
      const obstacleInTOF = shadingType === 'TOF' && obstacle;

      if (
        ![
          'panel',
          'setback',
          'points',
          'panel_points',
          'grid',
          'floor',
          'wallBorder',
          'sky',
          'sunSphere',
          'ambientLight'
        ].includes(obj.name) &&
        !obstacleInTOF
      ) {
        const newObj = this.cloneScene(shadingType, iterCount, !changeScene ? obj : parent.children[i], changeScene);
        if (newObj) {
          if (obj.name === 'sunLight') {
            const r = Math.max(1, (maxz - 100) / 50);
            this.sun = newObj;
            this.sun.color = new THREE.Color(1, 1, 1);
            this.sun.intensity = 1;
            this.sun.shadow.bias = -0.001;
            this.sun.shadow.radius = 0.0;
            this.sun.shadow.mapSize.height = 1024 * 2;
            this.sun.shadow.mapSize.width = 1024 * 2;
            this.sun.shadow.camera.far = 500;
            this.sun.shadow.camera.left = (-rwidth / 3) * 2;
            this.sun.shadow.camera.right = (rwidth / 3) * 2;
            this.sun.shadow.camera.top = (rheight / 7) * r * 2;
            this.sun.shadow.camera.bottom = (-rheight / 7) * r * 2;
            this.sun.shadow.camera.zoom = 1;
          } else if (obj.name === 'ambientLight') {
            this.ambient = newObj;
            this.ambient.color = new THREE.Color(1, 1, 1);
            this.ambient.intensity = 0;
          }

          newParent.add(newObj);
        }
      }
      newParent.remove(obj);
    }

    return newParent;
  };

  changeSceneMaterials = (scene, shadingType) => {
    this.cloneScene(shadingType, 1, scene, true, true);
  };

  moveSunManualy = (pos, changeTarget) => {
    this.sun.position.x = pos.x + this.sun.target.position.x;
    this.sun.position.y = pos.y + this.sun.target.position.y;
    this.sun.position.z = pos.z + this.sun.target.position.z;

    if (changeTarget) {
      this.sun.target.position.x = 0;
      this.sun.target.position.y = pos.y;
      this.sun.target.position.z = 0;
    } else {
      this.sun.target.position.x = 0;
      this.sun.target.position.y = 0;
      this.sun.target.position.z = 0;
    }
  };

  moveSun = (time, minutes) => {
    const pos = this.weather.getSunPosition(time);
    this.moveSunManualy(pos, false);
    const newTime = new Date(time);
    newTime.setTime(newTime.getTime() + minutes * 60 * 1000);
    return [newTime, pos];
  };

  getCanvasAndContext = (type, month) => {
    const ty = type.toLowerCase();
    const newCanvas = ty.startsWith('diffuse_')
      ? this.canvasPool.getCanvas(ty)
      : this.canvasPool.getCanvas(`${ty}_${month}`);
    const context = ty.startsWith('diffuse_')
      ? this.canvasPool.getContext(ty)
      : this.canvasPool.getContext(`${ty}_${month}`);
    return [newCanvas, context];
  };

  renderTargetToData = (renderer) => {
    const renderTarget = renderer.getRenderTarget();

    const pixelBuffer = new Uint8Array(renderTarget.width * renderTarget.height * 4);
    renderer.readRenderTargetPixels(renderTarget, 0, 0, renderTarget.width, renderTarget.height, pixelBuffer);

    const data = new Uint8Array(renderTarget.width * renderTarget.height * 4);
    for (let y = 0; y < renderTarget.height; y += 1) {
      for (let x = 0; x < renderTarget.width; x += 1) {
        let index = (y * renderTarget.width + x) * 4;
        const v = pixelBuffer[index];
        if (v < 0 || v > 255) console.log(`==== V: ${v}`);
        index = ((renderTarget.height - y - 1) * renderTarget.width + x) * 4;
        data[index] = v;
        data[++index] = v;
        data[++index] = v;
        data[++index] = 255;
      }
    }

    // if (this.renderer.shadowMap.enabled) {
    //   saveTextureAsImage(data, renderTarget.width, renderTarget.height)
    // }
    return data;
  };

  renderTargetToFloatData = (renderer) => {
    const rt = renderer.getRenderTarget();
    const w = rt.width,
      h = rt.height;
    const floatBuffer = new Float32Array(w * h * 4);
    renderer.readRenderTargetPixels(rt, 0, 0, w, h, floatBuffer);

    const single = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const srcIdx = (y * w + x) * 4;
        const dstIdx = (h - y - 1) * w + x;
        single[dstIdx] = floatBuffer[srcIdx];
      }
    }
    return single;
  };

  renderTargetToSingleChannel = (renderer) => {
    const rt = renderer.getRenderTarget();
    const w = rt.width,
      h = rt.height;
    if (rt.texture?.type !== THREE.FloatType) {
      const pixelBuffer = new Uint8Array(w * h * 4);
      renderer.readRenderTargetPixels(rt, 0, 0, w, h, pixelBuffer);

      const single = new Float32Array(w * h);
      for (let i = 0; i < w * h; i += 1) {
        single[i] = pixelBuffer[i * 4] / 255;
      }
      return single;
    }

    const floatBuffer = new Float32Array(w * h);
    renderer.readRenderTargetPixels(rt, 0, 0, w, h, floatBuffer);
    return floatBuffer;
  };

  renderTargetSize = (renderer) => {
    const renderTarget = renderer.getRenderTarget();
    return [renderTarget.width, renderTarget.height];
  };

  renderTargetToCanvas = (renderer, _) => {
    const renderTarget = renderer.getRenderTarget();
    const canvas = document.createElement('canvas');
    canvas.width = renderTarget.width;
    canvas.height = renderTarget.height;
    let ctx = canvas.getContext('2d');
    // Read pixel data from the render target
    const pixelBuffer = new Uint8Array(renderTarget.width * renderTarget.height * 4);
    renderer.readRenderTargetPixels(renderTarget, 0, 0, renderTarget.width, renderTarget.height, pixelBuffer);

    // Create an ImageData object from the pixel buffer
    const imageData = ctx.getImageData(0, 0, renderTarget.width, renderTarget.height);
    const { data } = imageData;
    for (let y = 0; y < renderTarget.height; y += 1) {
      for (let x = 0; x < renderTarget.width; x += 1) {
        let index = (y * renderTarget.width + x) * 4;
        const v = pixelBuffer[index];
        index = ((renderTarget.height - y - 1) * renderTarget.width + x) * 4;
        data[index] = v;
        data[++index] = v;
        data[++index] = v;
        data[++index] = 255;
      }
    }
    // Draw the ImageData onto the canvas
    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  dataToTexture = (data, width, height, floatType) => {
    let shadingTexture;
    if (floatType) {
      shadingTexture = new THREE.DataTexture(data, width, height, THREE.RedFormat, THREE.FloatType);
    } else {
      shadingTexture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat, THREE.UnsignedByteType);
    }
    shadingTexture.flipY = true;
    // shadingTexture.flipX = true;
    shadingTexture.encoding = THREE.LinearEncoding;
    shadingTexture.needsUpdate = true;
    return shadingTexture;
  };

  downloadSingleChannelPNG(fullTextureData, width, height, filename = 'texture.png') {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    const imgData = ctx.createImageData(width, height);
    const data = imgData.data; // Uint8ClampedArray

    for (let i = 0; i < fullTextureData.length; i++) {
      const v = Math.max(0, Math.min(1, fullTextureData[i]));
      const gray = Math.round(v * 255);
      const idx = i * 4;
      data[idx] = gray; // R
      data[idx + 1] = gray; // G
      data[idx + 2] = gray; // B
      data[idx + 3] = 255; // A
    }

    ctx.putImageData(imgData, 0, 0);

    canvas.toBlob((blob) => {
      const link = document.createElement('a');
      link.download = filename;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
    }, 'image/png');
  }

  downloadRGBChannelPNG(fullTextureData, width, height, filename = 'texture.png') {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    const imgData = ctx.createImageData(width, height);
    const data = imgData.data;

    for (let i = 0; i < fullTextureData.length; i++) {
      data[i] = fullTextureData[i];
    }

    ctx.putImageData(fullTextureData, 0, 0);

    canvas.toBlob((blob) => {
      const link = document.createElement('a');
      link.download = filename;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
    }, 'image/png');
  }

  renderTargetToCompleteData = (renderer) => {
    const data = this.renderTargetToSingleChannel(renderer);
    const fullTextureWidth = this.dsmTexture?.image?.width || 1024;
    const fullTextureHeight = this.dsmTexture?.image?.height || 1024;

    const fullTextureData = new Float32Array(fullTextureWidth * fullTextureHeight * this.channels);

    const [minx, miny, maxx, maxy] = this.ranges;
    const [dataWidth, dataHeight] = [maxx - minx, maxy - miny];

    this.placeImage(data, fullTextureData, dataWidth, dataHeight, fullTextureWidth, fullTextureHeight, minx, miny);

    // this.downloadSingleChannelPNG(fullTextureData, fullTextureWidth, fullTextureHeight, 'shading.png');

    return fullTextureData;
  };

  renderTargetToTexture = (renderer, floatType) => {
    const fullTextureData = this.renderTargetToCompleteData(renderer);
    const fullTextureWidth = this.dsmTexture?.image?.width || 1024;
    const fullTextureHeight = this.dsmTexture?.image?.height || 1024;
    let texture;
    if (floatType) {
      texture = new THREE.DataTexture(
        fullTextureData,
        fullTextureWidth,
        fullTextureHeight,
        THREE.RedFormat,
        THREE.FloatType
      );
    } else {
      texture = new THREE.DataTexture(
        fullTextureData,
        fullTextureWidth,
        fullTextureHeight,
        THREE.RGBAFormat,
        THREE.UnsignedByteType
      );
    }
    texture.flipY = true;
    texture.encoding = THREE.LinearEncoding;
    texture.needsUpdate = true;
    return texture;
  };

  getRenderTimes = (month) => {
    const irradianceWeekly = this.weather.getDnWeekly(month);
    const renderCountWeekly = irradianceWeekly.map((irw) => irw.map((ir) => (ir > 0 ? 1 : 0)).reduce((a, b) => a + b));
    return [renderCountWeekly, irradianceWeekly];
  };

  findBy = (objs, fieldName, name) => {
    for (let i = 0; i < objs.length; i += 1) {
      if (objs[i][fieldName] === name) {
        return objs[i];
      }
    }
    return null;
  };

  getCamera = () => {
    const [minx, miny, maxx, maxy] = this.ranges;
    const shadingCamera = new THREE.OrthographicCamera(
      -((maxx - minx) / 2) * this.scale,
      ((maxx - minx) / 2) * this.scale,
      ((maxy - miny) / 2) * this.scale,
      -((maxy - miny) / 2) * this.scale,
      0.1,
      1000
    );
    shadingCamera.position.set(
      (minx + (maxx - minx) / 2 - this.center[0]) * this.scale,
      100,
      (miny + (maxy - miny) / 2 - this.center[1]) * this.scale
    );
    shadingCamera.lookAt(shadingCamera.position.x, 0, shadingCamera.position.z);
    return shadingCamera;
  };

  exportAttributes = (scene, json, meshName, attrName) => {
    const attrs = {};
    scene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.name === meshName && attrName in child.geometry.attributes) {
        attrs[child.geometry.uuid] = child.geometry.attributes[attrName];
      }
    });
    for (let i = 0; i < json.geometries.length; i += 1) {
      const { uuid } = json.geometries[i];
      if (uuid in attrs) {
        if (!('data' in json.geometries[i])) json.geometries[i].data = {};
        if (!('attributes' in json.geometries[i].data)) json.geometries[i].data.attributes = {};
        json.geometries[i].data.attributes[attrName] = attrs[uuid];
      }
    }
  };

  exportScene = (scene, leaduid, data) => {
    const { imgSrc, dsmSrc } = data;
    const json = scene.toJSON();
    this.exportAttributes(scene, json, 'roofPlane', 'uv');
    const ground = this.findBy(json.object.children, 'name', 'ground').children[0];
    const dsmMat = this.findBy(json.materials, 'uuid', ground.material);
    const dsmTex = this.findBy(json.textures, 'uuid', dsmMat.displacementMap);
    const dsmImg = dsmTex.image;
    for (let i = 0; i < json.images.length; i += 1) {
      if (json.images[i].uuid === dsmImg) {
        json.images[i].url = dsmSrc;
      } else {
        json.images[i].url = imgSrc;
      }
    }
    const camera = this.getCamera();
    json.camera = camera.toJSON();
    json.lead = leaduid;
    json.initData = data;
    return json;
  };

  exportSunPositionAndIntensity = (renderPerHour = 4) => {
    const positions = [];
    const intensities = [];
    const irradiances = [];
    for (let month = 1; month <= 12; month += 1) {
      const monthPositions = [];
      const monthIntensities = [];
      const monthIrradiances = [];
      const [renderCountWeekly, irradianceWeekly] = this.getRenderTimes(month);
      const iterCount = renderCountWeekly.reduce((a, b) => a + b) * this.weather.renderPerHour;
      const sumBeemIrradiance = irradianceWeekly.map((rw) => rw.reduce((a, b) => a + b)).reduce((a, b) => a + b);
      // irradiances.push(sumBeemIrradiance);
      let sumIrradiance = 0;
      for (let weekIndex = 0; weekIndex < irradianceWeekly.length; weekIndex += 1) {
        const weekIrradiance = irradianceWeekly[weekIndex];
        for (let hourIndex = 0; hourIndex < weekIrradiance.length; hourIndex += 1) {
          const irradiance = weekIrradiance[hourIndex];
          if (irradiance > 0) {
            const count = irradiance > 0 ? renderPerHour : 1;
            for (let k = 0; k < count; k += 1) {
              const minute = k * (60.0 / renderPerHour);
              const sunPos = this.weather.getSunPositionWeekly(month, hourIndex, minute, weekIndex);
              const sunIntensity = (irradiance / (sumBeemIrradiance * renderPerHour)) * iterCount;
              sumIrradiance += sunIntensity;
              monthPositions.push([sunPos.x, sunPos.y, sunPos.z]);
              monthIntensities.push(sunIntensity);
              monthIrradiances.push(irradiance);
            }
          }
        }
      }
      positions.push(monthPositions);
      intensities.push(monthIntensities);
      irradiances.push(monthIrradiances);
    }
    return [positions, intensities, irradiances];
  };

  downloadContent = (content, filename, contentType) => {
    if (!contentType) contentType = 'application/octet-stream';
    const a = document.createElement('a');
    const blob = new Blob([content], { type: contentType });
    a.href = window.URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  shade = (scene, shadingType, month) => {
    if (this.byServerSideData) {
      return this.shadeByServerData(scene, shadingType, month);
    }

    if (this.debugMode) tic();
    const [renderCountWeekly, irradianceWeekly] = this.getRenderTimes(month);
    const [minx, miny, maxx, maxy] = this.ranges;

    const iterCount = renderCountWeekly.reduce((a, b) => a + b) * this.weather.renderPerHour;

    const shadingScene = this.cloneScene(shadingType, iterCount, scene, false, true);
    const [width, height] = [maxx - minx, maxy - miny];

    const shadingCamera = new THREE.OrthographicCamera(
      -((maxx - minx) / 2) * this.scale,
      ((maxx - minx) / 2) * this.scale,
      ((maxy - miny) / 2) * this.scale,
      -((maxy - miny) / 2) * this.scale,
      0.1,
      1000
    );
    shadingCamera.position.set(
      (minx + (maxx - minx) / 2 - this.center[0]) * this.scale,
      100,
      (miny + (maxy - miny) / 2 - this.center[1]) * this.scale
    );
    shadingCamera.lookAt(shadingCamera.position.x, 0, shadingCamera.position.z);

    this.renderer.autoClear = false;

    this.renderer.shadowMap.enabled = shadingType !== 'TOF';
    this.renderer.shadowMap.type = THREE.VSMShadowMap;
    this.renderer.preserveDrawingBuffer = true;
    this.renderer.setPixelRatio(1.0);
    this.renderer.setSize(width, height, true);
    this.renderer.clear(true, true, true);

    const renderTarget = this.newRenderer(width, height);
    this.renderer.setRenderTarget(renderTarget);

    if (this.ambient) this.ambient.intensity = 0;

    // const irradianceDnWeekly = this.weather.getDnWeekly(month);
    const sumBeemIrradiance = irradianceWeekly.map((rw) => rw.reduce((a, b) => a + b)).reduce((a, b) => a + b);

    // let sumIrradiance = 0;
    for (let weekIndex = 0; weekIndex < irradianceWeekly.length; weekIndex += 1) {
      const weekIrradiance = irradianceWeekly[weekIndex];
      for (let hourIndex = 0; hourIndex < weekIrradiance.length; hourIndex += 1) {
        const irradiance = weekIrradiance[hourIndex];
        if (irradiance > 0) {
          const count = irradiance > 0 ? this.weather.renderPerHour : 1;
          for (let k = 0; k < count; k += 1) {
            const minute = k * (60.0 / this.weather.renderPerHour);
            const sunPos = this.weather.getSunPositionWeekly(month, hourIndex, minute, weekIndex);
            this.moveSunManualy(sunPos, false);
            this.sun.intensity = (irradiance / (sumBeemIrradiance * this.weather.renderPerHour)) * iterCount;

            // sumIrradiance += this.sun.intensity;
            this.renderer.render(shadingScene, shadingCamera);
            this.renderer.clear(false, true, true);
          }
        }
      }
    }

    // const shadingTexture = this.renderTargetToTexture(this.renderer, shadingType, month);
    // downloadCanvas(shadingTexture.image, `${shadingType}_${month}.png`);

    const shadingData = this.renderTargetToCompleteData(this.renderer);

    this.renderer.setRenderTarget(null);

    if (this.debugMode) toc(`shade - ${shadingType} - ${month}`);
    return shadingData;
  };

  getAzimuthElevationFromPosition = (x, y, z) => {
    // radial distance
    const distance = Math.sqrt(x * x + y * y + z * z);

    // elevation in radians → degrees
    const elevation = Math.asin(y / distance) * (180 / Math.PI);

    // azimuth in radians → degrees: note we use atan2(x, -z) to match Python's atan2(x, -z)
    let azimuth = Math.atan2(x, -z) * (180 / Math.PI);

    // normalize to [0, 360)
    if (azimuth < 0) azimuth += 360;

    return [azimuth, elevation, distance];
  };

  getAzElFromVector = (v /*: THREE.Vector3 */) => {
    return getAzimuthElevationFromPosition(v.x, v.y, v.z);
  };

  getGroundDiffusePoints = (radius, counts) => {
    const uniquePoints = [];
    const weights = [];
    const elevations = [];
    const azimuths = [];
    let sumWeights = 0;

    // Generate points evenly around the ground circle
    for (let i = 0; i < counts; i++) {
      const angle = (2 * Math.PI * i) / counts;
      const x = radius * Math.cos(angle);
      const y = 0.0;
      const z = radius * Math.sin(angle);

      // Use THREE.Vector3 to represent the point
      const p = new THREE.Vector3(x, y, z);
      uniquePoints.push(p);

      // Compute spherical coords (azimuth, elevation)
      const [azimuth, elevation, d] = this.getAzimuthElevationFromPosition(p.x, p.y, p.z);
      azimuths.push(azimuth);
      elevations.push(elevation);

      // All ground points have constant weight of 1.0
      const w = 1.0;
      weights.push(w);
      sumWeights += w;
    }

    return {
      uniquePoints, // Array<THREE.Vector3>
      weights, // Array<number>
      sumWeights, // number
      elevations, // Array<number>
      azimuths // Array<number>
    };
  };

  get_sky_diffuse_points = (radius, detail) => {
    const points = [];
    const geo = new THREE.IcosahedronGeometry(radius, detail);
    const bath = new THREE.Mesh(geo, null);
    const count = bath.geometry.attributes.position.array.length / 9;
    const weights = [];
    for (let i = 0; i < count; i += 1) {
      const vert1 = new THREE.Vector3().fromArray(bath.geometry.attributes.position.array, i * 9 + 0 * 3);
      const vert2 = new THREE.Vector3().fromArray(bath.geometry.attributes.position.array, i * 9 + 1 * 3);
      const vert3 = new THREE.Vector3().fromArray(bath.geometry.attributes.position.array, i * 9 + 2 * 3);
      if (vert1.y >= 0) points.push(vert1);
      if (vert2.y >= 0) points.push(vert2);
      if (vert3.y >= 0) points.push(vert3);
    }

    const precision = 1e5;
    const seen = new Set();
    const uniquePoints = [];

    for (const p of points) {
      const key = `${Math.round(p.x * precision)},${Math.round(p.y * precision)},${Math.round(p.z * precision)}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniquePoints.push(p);
      }
    }

    // const uniquePoints = [...new Map(points.map((item) => [item.x + item.y / radius + item.z / (radius ** 2), item])).values()];

    let sumWeights = 0;
    for (let i = 0; i < uniquePoints.length; i += 1) {
      const p = uniquePoints[i];
      const w = 1.0 - Math.pow(p.y / radius, 1);
      weights.push(w);
      sumWeights += w;
    }

    return { uniquePoints, weights, sumWeights };
  };

  shadeDiffuse = (scene, shadingType) => {
    if (this.debugMode) tic();

    const radius = 250;
    const detail = 5;
    const horizon_n = 100;
    const iso_data = this.get_sky_diffuse_points(radius, detail);
    const horizon_data = this.getGroundDiffusePoints(radius, horizon_n);
    const diffuse_dataset = [iso_data, horizon_data];
    const diffuse_shade_results = [];

    for (let dt = 0; dt < diffuse_dataset.length; dt += 1) {
      const { uniquePoints, weights, sumWeights } = diffuse_dataset[dt];

      const iterCount = uniquePoints.length;
      const shadingScene = this.cloneScene(shadingType, iterCount, scene, false, true);

      const [minx, miny, maxx, maxy] = this.ranges;
      const [width, height] = [maxx - minx, maxy - miny];

      if (this.ambient) this.ambient.intensity = 0;

      const shadingCamera = new THREE.OrthographicCamera(
        -((maxx - minx) / 2) * this.scale,
        ((maxx - minx) / 2) * this.scale,
        ((maxy - miny) / 2) * this.scale,
        -((maxy - miny) / 2) * this.scale,
        0.1,
        1000
      );
      shadingCamera.position.set(
        (minx + (maxx - minx) / 2 - this.center[0]) * this.scale,
        100,
        (miny + (maxy - miny) / 2 - this.center[1]) * this.scale
      );
      shadingCamera.lookAt(shadingCamera.position.x, 0, shadingCamera.position.z);

      const renderTarget = this.newRenderer(width, height);

      this.renderer.shadowMap.enabled = shadingType !== 'TOF';
      this.renderer.shadowMap.type = THREE.VSMShadowMap;
      this.renderer.autoClear = false;
      this.renderer.state.setBlending(THREE.AdditiveBlending);
      // this.renderer.preserveDrawingBuffer = true;
      this.renderer.setPixelRatio(1);
      this.renderer.setSize(width, height, true);
      this.renderer.setRenderTarget(renderTarget);
      this.renderer.clear(true, true, true);
      this.renderer.setRenderTarget(null);

      let sumSunIntensity = 0;
      for (let i = 0; i < iterCount; i += 1) {
        this.sun.intensity = weights[i] / sumWeights;
        sumSunIntensity += this.sun.intensity;
        const pos = uniquePoints[i];
        this.moveSunManualy(pos, dt == 1);
        this.renderer.setRenderTarget(renderTarget);
        this.renderer.clearDepth();
        this.renderer.render(shadingScene, shadingCamera);
      }

      this.renderer.state.setBlending(THREE.NoBlending);
      this.renderer.autoClear = true;

      const diffuseData = this.renderTargetToCompleteData(this.renderer);
      diffuse_shade_results.push(diffuseData);

      // const shadingTexture = this.renderTargetToTexture(this.renderer, shadingType, 'diffuse');
      // downloadDataTextureAsPNG(shadingTexture, true, `${shadingType}_${dt}.png`);

      this.renderer.setRenderTarget(null);
    }

    if (this.debugMode) toc(`diffuse shade - ${shadingType}`);
    return diffuse_shade_results;
  };

  shadeByServerData = (scene, shadingType, month) => {
    if (this.debugMode) tic();
    const data = this.weather.getServerSideWeatherData();
    const monthData = data.irradiance_data[month - 1];
    const iterCount = 1;

    const [minx, miny, maxx, maxy] = this.ranges;

    const shadingScene = this.cloneScene(shadingType, iterCount, scene, false, true);
    const [width, height] = [maxx - minx, maxy - miny];

    const shadingCamera = new THREE.OrthographicCamera(
      -((maxx - minx) / 2) * this.scale,
      ((maxx - minx) / 2) * this.scale,
      ((maxy - miny) / 2) * this.scale,
      -((maxy - miny) / 2) * this.scale,
      0.1,
      1000
    );
    shadingCamera.position.set(
      (minx + (maxx - minx) / 2 - this.center[0]) * this.scale,
      100,
      (miny + (maxy - miny) / 2 - this.center[1]) * this.scale
    );
    shadingCamera.lookAt(shadingCamera.position.x, 0, shadingCamera.position.z);

    const renderTarget = this.newRenderer(width, height);

    this.renderer.shadowMap.enabled = shadingType !== 'TOF';
    this.renderer.shadowMap.type = THREE.VSMShadowMap;
    this.renderer.autoClear = false;
    this.renderer.state.setBlending(THREE.AdditiveBlending);
    // this.renderer.preserveDrawingBuffer = true;
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(width, height, true);
    this.renderer.setRenderTarget(renderTarget);
    this.renderer.clear(true, true, true);
    this.renderer.setRenderTarget(null);

    if (this.ambient) this.ambient.intensity = 0;

    let sumIrradiance = 0;
    const sum_dni = monthData.reduce((a, b) => a + b[3], 0);
    for (let i = 0; i < monthData.length; i += 1) {
      const [az, el, dhi, dni, ghi, albedo, extra_dni] = monthData[i];
      const az_radians = az * (Math.PI / 180);
      const el_radians = el * (Math.PI / 180);
      const sunPos = this.weather.getSunPositionByElevationAndAzimuth_v2(az_radians, el_radians);
      this.moveSunManualy(sunPos, false);
      this.sun.intensity = (dni / sum_dni) * iterCount;
      sumIrradiance += this.sun.intensity;
      this.renderer.setRenderTarget(renderTarget);
      this.renderer.clearDepth();
      this.renderer.render(shadingScene, shadingCamera);
    }

    this.renderer.state.setBlending(THREE.NoBlending);
    this.renderer.autoClear = true;

    const shadingData = this.renderTargetToCompleteData(this.renderer);

    // const shadingTexture = this.renderTargetToTexture(this.renderer, true);
    // downloadDataTextureAsPNG(shadingTexture, true, `${shadingType}_${month}.png`);

    this.renderer.setRenderTarget(null);

    if (this.debugMode) toc(`shade - ${shadingType} - ${month}`);
    return shadingData;
  };

  visiblePercentage = (pitchDegrees) => {
    return 1 - pitchDegrees / 180.0;
    const pitchRadians = (pitchDegrees * Math.PI) / 180;
    const visibility = (1 + Math.cos(pitchRadians)) / 2;
    return visibility;
  };

  calculateSolarAccessData = (
    tsrfData,
    tofData,
    dtsrfData_iso,
    dtofData_iso,
    dtsrfData_horizon,
    dtofData_horizon,
    month,
    width,
    height,
    pitchDegrees,
    segmentCount
  ) => {
    const contextMASK = this.canvasPool.getContext('GetMaskCanvas');
    const maskData = contextMASK.getImageData(0, 0, width, height);

    const indicesMaskContext = this.canvasPool.getContext(this.canvasPool.PoolKeys.SegmentIndexMask);
    const imageSegmentIndexData = indicesMaskContext.getImageData(0, 0, width, height);

    // const tsrfFloatData = new Float32Array(width * height * this.channels);
    // const tofFloatData = new Float32Array(width * height * this.channels);
    const saData = new Float32Array(width * height * this.channels);
    // const saData = new Uint8Array(width * height * 4);

    // this.downloadSingleChannelPNG(tsrfData, 1024, 1024, 'shading.png');
    // this.downloadRGBChannelPNG(maskData, 1024, 1024, 'mask.png');

    let sumBeemIrradiance;
    let sumDiffuseIrradiance;
    let sumDirectIrradiances = [];
    let sumDiffuseIrradiances_iso = [];
    let sumDiffuseIrradiances_horizon = [];
    let segments_tof = [];
    let optimalInsolation = 0;

    // const visibilityPercentage = [];

    if (this.byServerSideData) {
      const data = this.weather.getServerSideWeatherData();
      const monthData = data.irradiance_data[month - 1];
      const tofData = data.tof_data;
      optimalInsolation = data.opts[month - 1][2];
      // const sum_dni = monthData.reduce((a, b) => a + b[3], 0);
      // const sum_dhi = monthData.reduce((a, b) => a + b[2], 0);
      // sumBeemIrradiance = sum_dni;
      // sumDiffuseIrradiance = sum_dhi;
      for (let s = 0; s < segmentCount; s += 1) {
        const sum_dni = monthData.reduce((a, b) => a + b[7 + s], 0);
        const sum_dhi_iso = monthData.reduce((a, b) => a + b[7 + segmentCount + s], 0);
        const sum_dhi_horizon = monthData.reduce((a, b) => a + b[7 + 2 * segmentCount + s], 0);
        sumDirectIrradiances.push(sum_dni);
        sumDiffuseIrradiances_iso.push(sum_dhi_iso);
        sumDiffuseIrradiances_horizon.push(sum_dhi_horizon);
        segments_tof.push(tofData[s][month - 1]);
        // visibilityPercentage.push(this.visiblePercentage(pitchDegrees[s]));
      }
    } else {
      const irradianceDnWeekly = this.weather.getDnWeekly(month);
      const irradianceDfWeekly = this.weather.getDfWeekly(month);
      sumBeemIrradiance = irradianceDnWeekly.map((rw) => rw.reduce((a, b) => a + b)).reduce((a, b) => a + b);
      sumDiffuseIrradiance = irradianceDfWeekly.map((rw) => rw.reduce((a, b) => a + b)).reduce((a, b) => a + b);
      optimalInsolation = this.weather.optimalInsolations[month - 1];
    }

    const clamp01 = (value, fallback = 0) => {
      if (value !== value) {
        return fallback;
      }
      if (value < 0) {
        return 0;
      }
      if (value > 1.0) {
        return 1.0;
      }
      return value;
    };

    const safeOptimalInsolation = Number.isFinite(optimalInsolation) && optimalInsolation > 0 ? optimalInsolation : 0;

    if (this.byServerSideData) {
      segments_tof = segments_tof.map((value) => clamp01(value));
    }

    let sumSolarAcces = 0;
    let SACounetr = 0;

    const [minx, miny, maxx, maxy] = this.ranges;
    for (let x = minx; x <= maxx; x += 1) {
      for (let y = miny; y <= maxy; y += 1) {
        const maskIndex = y * (width * 4) + x * 4;
        const index = y * (width * this.channels) + x * this.channels;
        const maskValue = maskData.data[maskIndex];
        const segmentIndex = imageSegmentIndexData.data[maskIndex] - 1;
        if (maskValue !== 0 && segmentIndex >= 0 && segmentIndex < segmentCount) {
          // for (let ch = 0; ch < this.channels; ch += 1) {
          //   tsrfData[index + ch] = 0;
          //   tofData[index + ch] = 0;
          //   saData[index + ch] = 0;
          //   tsrfFloatData[index + ch] = 0;
          //   tofFloatData[index + ch] = 0;
          //   saFloatData[index + ch] = 0;
          // }
          // } else {
          const tsrfIntensity = tsrfData[index];
          const tofIntensity = tofData[index];
          const dtsrfIntensity_iso = dtsrfData_iso[index];
          const dtofIntensity_iso = dtofData_iso[index];
          const dtsrfIntensity_horizon = dtsrfData_horizon[index];
          const dtofIntensity_horizon = dtofData_horizon[index];
          let numerator;
          let denominator;

          if (true) {
            const directFactor = tofIntensity != 0 ? tsrfIntensity / tofIntensity : 0;
            const diffuseFactor_iso = dtofIntensity_iso != 0 ? dtsrfIntensity_iso / dtofIntensity_iso : 0;
            const diffuseFactor_horizon = dtofIntensity_horizon != 0 ? dtsrfIntensity_horizon / dtofIntensity_horizon : 0;
            numerator =
              sumDirectIrradiances[segmentIndex] * directFactor +
              sumDiffuseIrradiances_iso[segmentIndex] * diffuseFactor_iso +
              sumDiffuseIrradiances_horizon[segmentIndex] * diffuseFactor_horizon;
            denominator =
              sumDirectIrradiances[segmentIndex] +
              sumDiffuseIrradiances_iso[segmentIndex] +
              sumDiffuseIrradiances_horizon[segmentIndex];
          } else {
            // numerator = sumBeemIrradiance * tsrfIntensity + sumDiffuseIrradiance * dtsrfIntensity;
            // denominator = sumBeemIrradiance * tofIntensity + sumDiffuseIrradiance * dtofIntensity;
          }

          // const sa = denominator !== 0 ? Math.min(1, numerator / denominator) * 255 : 255;
          const saValueFloat = clamp01(denominator !== 0 ? numerator / denominator : 1, 1);

          sumSolarAcces += saValueFloat;
          SACounetr += 1;

          // [saData[index], saData[index + 1], saData[index + 2]] = [sa, sa, sa];
          // saData[index + 3] = 255;

          let tofValueFloat;
          let tsrfValueFloat;
          if (this.byServerSideData) {
            tofValueFloat = segments_tof[segmentIndex] ?? 0;
            tsrfValueFloat = clamp01(tofValueFloat * saValueFloat);
          } else if (safeOptimalInsolation > 0) {
            tofValueFloat = clamp01(denominator / safeOptimalInsolation);
            // if (tofValueFloat < 0 || tofValueFloat > 1) console.log(`==== tofValueFloat: ${tofValueFloat}`);
            tsrfValueFloat = clamp01(numerator / safeOptimalInsolation);
            // if (tsrfValueFloat < 0 || tsrfValueFloat > 1) console.log(`==== tsrfValueFloat: ${tsrfValueFloat}`);
          } else {
            tofValueFloat = 0;
            tsrfValueFloat = 0;
          }

          // const tsrfValue = tsrfValueFloat * 255;
          // const tofValue = tofValueFloat * 255;

          // console.log(`==== tsrfValue: ${tsrfValue}`);
          // console.log(`==== tofValue: ${tofValue}`);

          tsrfData[index] = tsrfValueFloat;
          tofData[index] = tofValueFloat;
          saData[index] = saValueFloat;

          // [tsrfData[index], tsrfData[index + 1], tsrfData[index + 2]] = [tsrfValue, tsrfValue, tsrfValue];
          // [tofData[index], tofData[index + 1], tofData[index + 2]] = [tofValue, tofValue, tofValue];

          // Float pipeline

          // [tsrfFloatData[index], tsrfFloatData[index + 1], tsrfFloatData[index + 2]] = [tsrfValueFloat, tsrfValueFloat, tsrfValueFloat, 1.0];
          // [tofFloatData[index], tofFloatData[index + 1], tofFloatData[index + 2]] = [tofValueFloat, tofValueFloat, tofValueFloat, 1.0];
          // [saFloatData[index], saFloatData[index + 1], saFloatData[index + 2], saFloatData[index + 3]] = [saFloat, saFloat, saFloat, 1.0];
        }
      }
    }

    const averageSolarAccess = SACounetr > 0 ? sumSolarAcces / SACounetr : 1;
    sumSolarAcces = clamp01(averageSolarAccess, 1);

    return [tsrfData, tofData, saData, sumSolarAcces];
  };

  calculateAverageTextures = (materials, shader, shadeData, width, height, segmentCount, setTextures = true) => {
    tic();

    const contextMASK = this.canvasPool.getContext('GetMaskCanvas');
    const maskData = contextMASK.getImageData(0, 0, width, height);

    const indicesMaskContext = this.canvasPool.getContext(this.canvasPool.PoolKeys.SegmentIndexMask);
    const imageSegmentIndexData = indicesMaskContext.getImageData(0, 0, width, height);

    const dataFloatTSRF = new Float32Array(width * height * this.channels);
    const dataFloatTOF = new Float32Array(width * height * this.channels);
    const dataFloatSA = new Float32Array(width * height * this.channels);

    const dataTSRF = new Uint8Array(width * height * 4);
    const dataTOF = new Uint8Array(width * height * 4);
    const dataSA = new Uint8Array(width * height * 4);
    const dataGTSRF = new Uint8Array(width * height * 4);
    const dataGTOF = new Uint8Array(width * height * 4);
    const dataGSA = new Uint8Array(width * height * 4);

    // const sumIrradiance = [];
    // let totalIrradiance = 0;

    const data = [];
    let optimalInsolationInYear = 0;
    let monthOptimalInsolation = [];

    for (let i = 1; i <= 12; i += 1) {
      const tsrfFloatData = shadeData[i][0];
      const tofFloatData = shadeData[i][1];
      const saFloatData = shadeData[i][2];

      data.push([tsrfFloatData, tofFloatData, saFloatData]);

      // if (this.byServerSideData) {
      // } else {
      //   // const irradianceDnWeekly = this.weather.getDnWeekly(i);
      //   // const irradianceDfWeekly = this.weather.getDfWeekly(i);
      //   // const sumBeemIrradiance = irradianceDnWeekly.map((rw) => rw.reduce((a, b) => a + b)).reduce((a, b) => a + b);
      //   // const sumDiffuseIrradiance = irradianceDfWeekly.map((rw) => rw.reduce((a, b) => a + b)).reduce((a, b) => a + b);
      //   // sumIrradiance.push(sumBeemIrradiance + sumDiffuseIrradiance);
      // }
    }

    let tof_data;
    if (this.byServerSideData) {
      const data = this.weather.getServerSideWeatherData();
      tof_data = data.tof_data;
      optimalInsolationInYear = data.opts[data.opts.length - 1][2];
      monthOptimalInsolation = data.opts.map((opt) => opt[2]);
    } else {
      // totalIrradiance = sumIrradiance.reduce((a, b) => a + b);
      monthOptimalInsolation = this.weather.optimalInsolations;
      optimalInsolationInYear = monthOptimalInsolation.reduce((a, b) => a + b);
    }

    const [minx, miny, maxx, maxy] = this.ranges;
    for (let x = minx; x <= maxx; x += 1) {
      for (let y = miny; y <= maxy; y += 1) {
        const maskIndex = y * (width * 4) + x * 4;
        const maskValue = maskData.data[maskIndex];
        const segmentIndex = imageSegmentIndexData.data[maskIndex] - 1;
        if (maskValue === 0 || !Number.isInteger(segmentIndex) || segmentIndex < 0 || segmentIndex >= segmentCount) {
          dataGTSRF[maskIndex + 3] = 0;
          dataGTOF[maskIndex + 3] = 0;
          dataGSA[maskIndex + 3] = 0;
        } else {
          const index = y * (width * this.channels) + x * this.channels;
          // let tsrfIntensity = 0;
          // let tofIntensity = 0;
          // let saIntensity = 0;

          let tsrfFloatIntensity = 0;
          let tofFloatIntensity = 0;
          // let saFloatIntensity = 0;

          for (let i = 1; i <= 12; i += 1) {
            const [tsrfFloatData, tofFloatData, saFloatData] = data[i - 1];
            // tsrfIntensity += tsrfData[index] * monthOptimalInsolation[i - 1];
            // tofIntensity += tofData[index] * monthOptimalInsolation[i - 1];
            // saIntensity += solarAccessData[index] * sumIrradiance[i - 1];

            tsrfFloatIntensity += tsrfFloatData[index] * monthOptimalInsolation[i - 1];
            tofFloatIntensity += tofFloatData[index] * monthOptimalInsolation[i - 1];
            // saFloatIntensity += solarAccessFloatData[index] * sumIrradiance[i - 1];
          }

          let avgFloatTSRF = Math.min(1.0, tsrfFloatIntensity / optimalInsolationInYear);
          let avgFloatTOF = Math.min(1.0, tofFloatIntensity / optimalInsolationInYear);
          let avgFloatSA = avgFloatTOF !== 0 ? Math.min(1.0, avgFloatTSRF / avgFloatTOF) : 0;

          if (this.byServerSideData) {
            const serverAnnualTOF = Number(tof_data?.[segmentIndex]?.[12]);
            if (Number.isFinite(serverAnnualTOF)) {
              avgFloatTOF = Math.min(1.0, serverAnnualTOF);
              avgFloatTSRF = Math.min(1.0, avgFloatTOF * avgFloatSA);
            }
          }

          const avgTSRF = avgFloatTSRF * 255;
          dataTSRF[maskIndex] = avgTSRF;
          dataTSRF[maskIndex + 1] = avgTSRF;
          dataTSRF[maskIndex + 2] = avgTSRF;
          dataTSRF[maskIndex + 3] = 255;
          dataFloatTSRF[index] = avgFloatTSRF;
          // dataFloatTSRF[index + 1] = avgFloatTSRF;
          // dataFloatTSRF[index + 2] = avgFloatTSRF;
          // dataFloatTSRF[index + 3] = 1.0;
          const gTSRF = gray2gradient(avgFloatTSRF);
          [dataGTSRF[maskIndex], dataGTSRF[maskIndex + 1], dataGTSRF[maskIndex + 2]] = gTSRF;
          dataGTSRF[maskIndex + 3] = 255;

          let avgTOF = avgFloatTOF * 255;
          dataTOF[maskIndex] = avgTOF;
          dataTOF[maskIndex + 1] = avgTOF;
          dataTOF[maskIndex + 2] = avgTOF;
          dataTOF[maskIndex + 3] = 255;
          dataFloatTOF[index] = avgFloatTOF;
          // dataFloatTOF[index + 1] = avgFloatTOF;
          // dataFloatTOF[index + 2] = avgFloatTOF;
          // dataFloatTOF[index + 3] = 1.0;
          const gTOF = gray2gradient(avgFloatTOF);
          [dataGTOF[maskIndex], dataGTOF[maskIndex + 1], dataGTOF[maskIndex + 2]] = gTOF;
          dataGTOF[maskIndex + 3] = 255;

          // const avgSA = saIntensity / totalIrradiance;
          // const avgFloatSA = saFloatIntensity / totalIrradiance;

          const avgSA = avgFloatSA * 255;
          dataSA[maskIndex] = avgSA;
          dataSA[maskIndex + 1] = avgSA;
          dataSA[maskIndex + 2] = avgSA;
          dataSA[maskIndex + 3] = 255;
          dataFloatSA[index] = avgFloatSA;
          // dataFloatSA[index + 1] = avgFloatSA;
          // dataFloatSA[index + 2] = avgFloatSA;
          // dataFloatSA[index + 3] = 1.0;
          const gSA = gray2gradient(avgFloatSA);
          [dataGSA[maskIndex], dataGSA[maskIndex + 1], dataGSA[maskIndex + 2]] = gSA;
          dataGSA[maskIndex + 3] = 255;
        }
      }
    }

    shadeData[0] = [dataFloatTSRF, dataFloatTOF, dataFloatSA];

    if (setTextures) {
      console.log('set textures');
      const TSRFTexture = shader.dataToTexture(dataTSRF, width, height);
      const TOFTexture = shader.dataToTexture(dataTOF, width, height);
      const SATexture = shader.dataToTexture(dataSA, width, height);
      const GTSRFTexture = shader.dataToTexture(dataGTSRF, width, height);
      const GTOFTexture = shader.dataToTexture(dataGTOF, width, height);
      const GSATexture = shader.dataToTexture(dataGSA, width, height);

      materials.setTSRFTextures(0, TSRFTexture, GTSRFTexture);
      materials.setTOFTextures(0, TOFTexture, GTOFTexture);
      materials.setSolarAccessTextures(0, SATexture, GSATexture);
    }

    toc('calculateAverageTextures');
  };

  calculateShadingFactor = async (shadingData, segmentCount) => {
    tic();

    const indicesMaskCanvas = this.canvasPool.getCanvas(this.canvasPool.PoolKeys.SegmentIndexMask);
    const indicesMaskContext = this.canvasPool.getContext(this.canvasPool.PoolKeys.SegmentIndexMask);
    const imageSegmentIndexData = indicesMaskContext.getImageData(0, 0, indicesMaskCanvas.width, indicesMaskCanvas.height);
    const { width } = indicesMaskCanvas;

    const [tsrfAccumulator, tofAccumulator, saAccumulator, counts] = [[], [], [], []];

    for (let s = 0; s < segmentCount; s += 1) {
      tsrfAccumulator.push(new Array(13).fill(0));
      tofAccumulator.push(new Array(13).fill(0));
      saAccumulator.push(new Array(13).fill(0));
      counts.push(new Array(13).fill(0));
    }

    const [minx, miny, maxx, maxy] = this.ranges;
    for (let x = minx; x <= maxx; x += 1) {
      for (let y = miny; y <= maxy; y += 1) {
        const maskIndex = y * (width * 4) + x * 4;
        const index = y * (width * this.channels) + x * this.channels;
        const segmentIndex = imageSegmentIndexData.data[maskIndex] - 1;
        if (segmentIndex >= 0 && segmentIndex < segmentCount) {
          // TODO: it was m = 0 before, but I changed to m = 1
          for (let m = 0; m <= 12; m += 1) {
            const [tsrfFloatMonthData, tofFloatMonthData, saFloatMonthData] = shadingData[m];
            tsrfAccumulator[segmentIndex][m] += tsrfFloatMonthData[index];
            tofAccumulator[segmentIndex][m] += tofFloatMonthData[index];
            saAccumulator[segmentIndex][m] += saFloatMonthData[index];
            counts[segmentIndex][m] += 1;
          }
        }
      }
    }

    const [tsrfResult, tofResult, saResult] = [[], [], []];
    for (let s = 0; s < segmentCount; s += 1) {
      tsrfResult.push([]);
      tofResult.push([]);
      saResult.push([]);

      for (let m = 0; m <= 12; m += 1) {
        tsrfResult[s].push(tsrfAccumulator[s][m] / counts[s][m]);
        tofResult[s].push(tofAccumulator[s][m] / counts[s][m]);
        saResult[s].push(saAccumulator[s][m] / counts[s][m]);
      }

      tsrfResult[s].push(tsrfResult[s].shift());
      tofResult[s].push(tofResult[s].shift());
      saResult[s].push(saResult[s].shift());
    }

    toc('calculateShadingFactor');

    return [tsrfResult, tofResult, saResult];
  };
}

function saveTextureAsImage(fullTextureData, width, height) {
  // Create a canvas element
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  // Create an ImageData object using the texture data
  const imageData = new ImageData(new Uint8ClampedArray(fullTextureData), width, height);

  // Put the ImageData into the canvas
  ctx.putImageData(imageData, 0, 0);

  // Convert the canvas to a Blob
  canvas.toBlob(function (blob) {
    // Create an anchor element and set the URL as the Blob URL
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style = 'display: none';
    a.href = URL.createObjectURL(blob);
    a.download = 'texture.png'; // Name the download file

    // Trigger the download
    a.click();

    // Clean up the URL object and remove the anchor element
    window.URL.revokeObjectURL(a.href);
    document.body.removeChild(a);
  });
}
